import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { isProductionEnvironment } from '@hungryroot/environment';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  createTestCustomer,
  householdSize,
  OriginEnum,
  StatusCode,
  type CreateTestCustomerParams,
} from '@hungryroot/rest';

/**
 * For use in non-prod environments. Allows rapid creation fo test customers in a variety of states.
 */
export function useCreateTestCustomer() {
  if (isProductionEnvironment()) {
    throw new Error('useCreateTestCustomer cannot be used in production');
  }

  return useMutation({
    mutationFn: async (params: CreateTestCustomerParams) => {
      const resp = await createTestCustomer(params);

      const { status: respStatus } = resp;
      if (respStatus !== StatusCode.SuccessCreated) {
        logUnknownErrorResponse('Failed to create test customer', {
          payload: params,
          key: 'useCreateTestCustomer.createTestCustomer.error',
          response: resp,
        });

        throw new Error('Failed to create test customer');
      }

      return resp.data;
    },
  });
}

export const CreateTestCustomerSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    first_name: z.string().min(1, 'First name is required').default('Jane'),
    last_name: z.string().min(1, 'Last name is required').default('Doe'),
    frequency: z.coerce.number().default(7),
    day_of_week: z.coerce.number().default(0),
    origin: z.coerce.number().default(OriginEnum.East),
    experiments: z
      .array(
        z.object({
          experiment_id: z.coerce.number(),
          variant: z.coerce.number(),
        })
      )
      .default([]),
    household_size: z.coerce.number().default(householdSize.ME),
    num_adults: z.coerce
      .number({
        invalid_type_error: 'Number of adults must be a number',
      })
      .min(1)
      .default(1),
    num_young_children: z.coerce
      .number({
        invalid_type_error: 'Number of young children must be a number',
      })
      .min(0)
      .default(0),
    num_older_children: z.coerce
      .number({
        invalid_type_error: 'Number of older children must be a number',
      })
      .min(0)
      .default(0),
    weekly_grocery_spend: z.coerce
      .number({
        invalid_type_error: 'Weekly grocery spend must be a number',
      })
      .min(0)
      .default(100),
    breakfast_meal_snack: z.coerce
      .number({
        invalid_type_error: 'Breakfast meal snack must be a number',
      })
      .min(0)
      .default(3),
    lunch_meal_snack: z.coerce
      .number({
        invalid_type_error: 'Lunch meal snack must be a number',
      })
      .min(0)
      .default(3),
    dinner_meal_snack: z.coerce
      .number({
        invalid_type_error: 'Dinner meal snack must be a number',
      })
      .min(0)
      .default(3),
    dietary_restrictions: z.coerce.number().default(1),
    breakfast_frequency: z.coerce
      .number({
        invalid_type_error: 'Breakfast frequency must be a number',
      })
      .min(0)
      .default(2),
    lunch_frequency: z.coerce
      .number({
        invalid_type_error: 'Lunch frequency must be a number',
      })
      .min(0)
      .default(2),
    dinner_frequency: z.coerce
      .number({
        invalid_type_error: 'Dinner frequency must be a number',
      })
      .min(0)
      .default(2),
    address1: z.string().min(1, 'Address is required').default('123 Main St'),
    city: z.string().min(1, 'City is required').default('New York'),
    state: z.string().min(1, 'State is required').default('NY'),
    zip_code: z
      .string()
      .min(1, 'ZIP code is required')
      .max(5, 'ZIP code must be 5 digits')
      .default('10001'),
    checkout: z
      .union([z.boolean(), z.string()])
      .default(false)
      .transform((val) => {
        if (typeof val === 'string') {
          return val === 'true';
        }

        return val;
      }),
    use_reward_credits: z
      .union([z.boolean(), z.string()])
      .default(false)
      .transform((val) => {
        if (typeof val === 'string') {
          return val === 'true';
        }
        return val;
      }),
    delivered_orders: z.coerce
      .number({
        invalid_type_error: 'Delivered orders must be a number',
      })
      .min(0)
      .default(0),
    days_between_orders: z.coerce
      .number({
        invalid_type_error: 'Days between orders must be a number',
      })
      .min(1)
      .default(7),
    rollover_credits: z.coerce
      .number({
        invalid_type_error: 'Rollover credits must be a number',
      })
      .min(0)
      .default(0),
    life_promo: z
      .enum(['NONE', 'CYOVEGGIES', 'CYOCOOKIEDOUGH', 'CYOPROTEIN'])
      .default('NONE'),
    referral_code: z.string().trim().optional(),
    phone: z
      .string()
      .trim()
      .transform((val) => val.replace(/\D/g, ''))
      .refine(
        (val) => val === '' || val.length === 10,
        'Must be a 10-digit US phone number'
      )
      .optional(),
    sms_opt_in: z
      .union([z.boolean(), z.string()])
      .default(false)
      .transform((val) => {
        if (typeof val === 'string') {
          return val === 'true';
        }
        return val;
      }),
  })
  .superRefine((data, ctx) => {
    if (data.sms_opt_in && !data.phone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Phone number is required when opting into SMS notifications',
        path: ['phone'],
      });
    }
  });
